import React from 'react';
import { Box, BoxProps, useTheme } from '@mui/material';
import { DetailsSubheader } from '../DetailsSubheader';
export interface BasicPageTemplateProps {
  pageTitle?: React.ReactNode | string;
  isSmallDevice: boolean;
  children?: JSX.Element | JSX.Element[];
  testId?: string;
  isReturn?: boolean;
  smallDeviceTopRight?: JSX.Element | JSX.Element[];
  containerSx?: BoxProps['sx'];
  submitButton?: React.ReactNode;
  actionButtons?: React.ReactNode[];
  t: (key: string, options?: Record<string, string | number>) => string;
  optionsDropdown?: JSX.Element;
  subtitle?: string;
}

/**
 * Renders a minimal generic page template that helps drive consistency in terms of spacing, title size & return buttons
 *
 * @param pageTitle - (optional) main title for the page
 * @param isSmallDevice - small viewport or not
 * @param children - (optional) page contents
 * @param testId - the value for the 'data-testid' attribute
 * @param isReturn - return button is return property
 * @param smallDeviceTopRight - (optional) on  for rendering an element opposite the mobile back button (small devices ONLY)
 * @param submitButton - (optional) Usually the RJSF submit button to show on the top corner on all viewports
 * @param t - translation function
 */
export const BasicPageTemplate: React.FC<React.PropsWithChildren<BasicPageTemplateProps>> = ({
  pageTitle,
  isSmallDevice,
  children,
  testId,
  submitButton,
  optionsDropdown,
  actionButtons,
  subtitle = '',
}: BasicPageTemplateProps) => {
  const {
    custom: { spacing },
  } = useTheme();
  const hasControls = !!optionsDropdown || !!submitButton || !!actionButtons;
  const Controls: JSX.Element = hasControls ? (
    <>
      {optionsDropdown && !(!isSmallDevice && pageTitle) ? optionsDropdown : <></>}
      {!!submitButton && submitButton}
      {!!optionsDropdown && optionsDropdown}
      {actionButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: spacing?.lg }}>
          {actionButtons.map((actionButton, index) => (
            <Box key={index}>{actionButton}</Box>
          ))}
        </Box>
      )}
    </>
  ) : null;

  return (
    <Box data-testid={`basic-page-template${testId ? `-${testId}` : ''}`} height="100%">
      {typeof pageTitle === 'string' ? (
        <DetailsSubheader title={pageTitle} controls={Controls} subtitle={subtitle} />
      ) : (
        <Box mb={4}>{pageTitle}</Box>
      )}
      {children}
    </Box>
  );
};
